import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { ErrorMessage, Field } from "@Light/components/system/Fieldset";
import { Input } from "@Light/components/system/Input";
import { useForm } from "react-hook-form";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { FieldLabel } from "../FieldLabel";
import { useEnrollment } from "../flow/context";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";

type UtilityAccountNumberForm = {
  utility_account_number: string;
};

export const ManualUtilityAccountNumber = () => {
  const { requirements, next, back, nextMutation } = useEnrollment();
  const stepper = useEnrollmentStepper(EnrollRoute.ManualUtilityAccountNumber);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UtilityAccountNumberForm>();
  const utilConfig = useEnrollmentUtilityConfiguration();

  const onSubmit = handleSubmit(async (data) => {
    await next({
      utility_account_number: { value: data.utility_account_number.trim() },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <PageBody
        stepper={stepper}
        title={`What’s your ${utilConfig.accountNumberName}?`}
        subtitle="We’ll use this to connect directly to your meter."
        onBack={back}
        footer={
          <EnrollHelpButtonFooter>
            <MutationButton
              size="lg"
              mutation={nextMutation}
              mutateButtonText="Continue"
              errorMessage="Failed saving your utility account number. Please try again later"
              data-testid="manual-utility-account-number-submit"
            />
          </EnrollHelpButtonFooter>
        }
      >
        <div className="flex flex-col gap-6 pt-4">
          <Field className="flex flex-col gap-1">
            <FieldLabel className="sr-only">
              {utilConfig.accountNumberName}
            </FieldLabel>
            <Input
              type="text"
              placeholder="12345-67890"
              isError={!!errors.utility_account_number}
              data-record="true"
              data-testid="manual-utility-account-number-input"
              {...register("utility_account_number", {
                required: true,
                value: requirements.utility_account_number?.value?.value ?? "",
              })}
            />
            <ErrorMessage error={errors.utility_account_number?.message} />
          </Field>

          {utilConfig.accountNumberDescription ? (
            <div className="flex flex-col gap-1 text-xs">
              <div className="font-semibold text-primary">
                Where do I find my {utilConfig.accountNumberName}?
              </div>
              <div className="text-tertiary">
                {utilConfig.accountNumberDescription}
              </div>
            </div>
          ) : null}
        </div>
      </PageBody>
    </form>
  );
};
